import React, { useEffect, useCallback, useState } from 'react'
import { createPortal } from 'react-dom'
import Button from './Button'
import Icon from '../AppIcon'

export default function ConfirmModal({
  open,
  title = 'Confirm',
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'destructive',
  icon = 'AlertTriangle',
  onConfirm,
  onCancel,
}) {
  const [exiting, setExiting] = useState(false)

  const handleClose = useCallback(() => {
    setExiting(true)
    setTimeout(() => {
      setExiting(false)
      onCancel?.()
    }, 200)
  }, [onCancel])

  const handleConfirm = useCallback(() => {
    setExiting(true)
    setTimeout(() => {
      setExiting(false)
      onConfirm?.()
    }, 200)
  }, [onConfirm])

  // Close on Escape
  useEffect(() => {
    if (!open) return
    const handler = (e) => { if (e.key === 'Escape') handleClose() }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [open, handleClose])

  if (!open) return null

  const iconColor = variant === 'destructive' ? 'text-destructive' : 'text-primary'

  return createPortal(
    <div
      className={`
        fixed inset-0 z-[9998] flex items-center justify-center p-4
        transition-opacity duration-200
        ${exiting ? 'opacity-0' : 'opacity-100'}
      `}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40"
        onClick={handleClose}
      />

      {/* Modal */}
      <div
        className={`
          relative w-full max-w-sm
          bg-popover/95
          border border-border
          rounded-2xl shadow-elevation-4
          transition-all duration-200
          ${exiting ? 'scale-95 opacity-0' : 'animate-scale-in'}
        `}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-4">
          {/* Icon */}
          <div className={`w-11 h-11 mx-auto mb-3.5 rounded-full flex items-center justify-center ${variant === 'destructive' ? 'bg-destructive/12' : 'bg-primary/12'}`}>
            <Icon name={icon} size={20} className={iconColor} />
          </div>

          {/* Title */}
          <h3 className="text-base font-semibold text-foreground text-center mb-1.5">
            {title}
          </h3>

          {/* Message */}
          <p className="text-sm text-muted-foreground text-center leading-relaxed">
            {message}
          </p>
        </div>

        {/* Actions — Apple stacked style */}
        <div className="border-t border-border">
          <button
            className={`w-full py-3 text-sm font-semibold border-b border-border transition-colors hover:bg-muted/40 ${variant === 'destructive' ? 'text-destructive' : 'text-primary'}`}
            onClick={handleConfirm}
          >
            {confirmLabel}
          </button>
          <button
            className="w-full py-3 text-sm font-medium text-foreground/70 transition-colors hover:bg-muted/40 rounded-b-2xl"
            onClick={handleClose}
          >
            {cancelLabel}
          </button>
        </div>
      </div>
    </div>,
    document.body
  )
}
